import React, { useEffect, useRef, useState } from 'react';
import SelectField from '../components/SelectField';
import InfoCard from '../components/InfoCard';
import LoadingState, { InlineSpinner } from '../components/LoadingState';
import { useNavigate } from 'react-router-dom';
import { copyFiles, getCrossS3, listCrossS3Buckets } from '../api/api';
import { FaCloud, FaFolder, FaFolderOpen, FaCheckCircle, FaTimesCircle, FaUpload, FaLink, FaCopy } from 'react-icons/fa';

// The stack's own region is the natural default source region — parsed from
// the API endpoint (https://xxx.execute-api.<region>.amazonaws.com/...).
const stackRegion = () => {
  const m = String(window.pcaSettings?.api?.uri || '').match(/execute-api\.([a-z0-9-]+)\./);
  return m ? m[1] : 'us-east-1';
};

const FileCopy = ({ setAlert }) => {
  const navigate = useNavigate();
  const [sourceType, setSourceType] = useState('s3');
  const [sourceConfig, setSourceConfig] = useState({
    bucket: '',
    region: stackRegion(),
    prefix: '',
    ftpHost: '',
    ftpPort: '21',
    ftpUsername: '',
    ftpPassword: '',
    ftpPath: '/'
  });
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [bucketContents, setBucketContents] = useState([]);
  const [loadingContents, setLoadingContents] = useState(false);
  const [contentsLoaded, setContentsLoaded] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [contentsNextToken, setContentsNextToken] = useState(null);
  const [firstPageNextToken, setFirstPageNextToken] = useState(null);
  const [loadingMoreContents, setLoadingMoreContents] = useState(false);
  // Region-scoped bucket picker
  const [regionBuckets, setRegionBuckets] = useState([]);
  const [bucketsLoading, setBucketsLoading] = useState(false);
  const [bucketsError, setBucketsError] = useState(null);
  const [bucketSearchOpen, setBucketSearchOpen] = useState(false);
  const bucketBoxRef = useRef(null);

  useEffect(() => {
    if (sourceType !== 's3') return;
    let cancelled = false;
    const loadBuckets = async () => {
      setBucketsLoading(true);
      setBucketsError(null);
      try {
        const res = await listCrossS3Buckets(sourceConfig.region);
        if (!cancelled) setRegionBuckets(res?.buckets || []);
      } catch (e) {
        console.error('Failed to list buckets', e);
        if (!cancelled) {
          setRegionBuckets([]);
          setBucketsError(e?.message || 'Failed to list buckets for this region.');
        }
      } finally {
        if (!cancelled) setBucketsLoading(false);
      }
    };
    loadBuckets();
    return () => { cancelled = true; };
  }, [sourceConfig.region, sourceType]);

  // Close the bucket suggestions on outside click
  useEffect(() => {
    if (!bucketSearchOpen) return;
    const onDown = (e) => {
      if (bucketBoxRef.current && !bucketBoxRef.current.contains(e.target)) {
        setBucketSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [bucketSearchOpen]);

  // Smart search: prefix matches first, then substring matches.
  const bucketMatches = (() => {
    const q = sourceConfig.bucket.trim().toLowerCase();
    const names = regionBuckets.map((b) => b.name);
    if (!q) return names;
    const starts = names.filter((n) => n.toLowerCase().startsWith(q));
    const contains = names.filter((n) => !n.toLowerCase().startsWith(q) && n.toLowerCase().includes(q));
    return [...starts, ...contains];
  })();

  const handleInputChange = (field, value) => {
    setSourceConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const testConnection = async () => {
    setIsConnecting(true);
    setConnectionStatus(null);

    try {
      if (sourceType === 's3') {
        // Validate required fields first
        if (!sourceConfig.bucket) {
          throw new Error('S3 bucket is required');
        }

        // Attempt to list the bucket using the API to verify access
        await getCrossS3(sourceConfig.bucket, sourceConfig.region);
      } else {
        if (!sourceConfig.ftpHost || !sourceConfig.ftpUsername) {
          throw new Error('FTP host and username are required');
        }

        // No FTP backend exists yet — fail explicitly instead of faking success.
        throw new Error('FTP connections are not supported yet.');
      }

      setConnectionStatus('success');
      setAlert({
        variant: 'success',
        heading: 'Connection Successful',
        text: 'Successfully connected to the source location.'
      });

    } catch (error) {
      setConnectionStatus('error');
      setAlert({
        variant: 'error',
        heading: 'Connection Failed',
        text: error.message || 'Failed to connect to the source location.'
      });
    } finally {
      setIsConnecting(false);
    }
  };

  const loadBucketContents = async (bucketOverride, { append = false } = {}) => {
    const bucket = bucketOverride || sourceConfig.bucket;
    const token = append ? contentsNextToken : null;
    (append ? setLoadingMoreContents : setLoadingContents)(true);
    try {
      const res = await getCrossS3(bucket, sourceConfig.region, sourceConfig.prefix, token);
      // Paged shape { files, nextToken }; tolerate the legacy bare array.
      const files = Array.isArray(res) ? res : res?.files || [];
      const nextToken = Array.isArray(res) ? null : res?.nextToken || null;
      setBucketContents(prev => (append ? [...prev, ...files] : files));
      setContentsNextToken(nextToken);
      if (!append) setFirstPageNextToken(nextToken);
      setContentsLoaded(true);
    } catch (err) {
      setAlert({
        variant: 'error',
        heading: 'Unable to Load Bucket',
        text: err.message || 'Failed to retrieve bucket contents.'
      });
    } finally {
      (append ? setLoadingMoreContents : setLoadingContents)(false);
    }
  };

  const toggleFile = (item) => {
    setSelectedFiles(prev =>
      prev.some(f => f.Key === item.Key)
        ? prev.filter(f => f.Key !== item.Key)
        : [...prev, item]
    );
  };

  const allSelected = bucketContents.length > 0 && selectedFiles.length === bucketContents.length;

  const toggleSelectAll = () => {
    setSelectedFiles(allSelected ? [] : [...bucketContents]);
  };

  const startCopy = async () => {
    if (connectionStatus !== 'success') {
      setAlert({
        variant: 'warning',
        heading: 'Test Connection First',
        text: 'Please test the connection before starting the copy operation.'
      });
      return;
    }

    if (selectedFiles.length === 0) {
      setAlert({
        variant: 'warning',
        heading: 'No Files Selected',
        text: 'Please select at least one file to copy.'
      });
      return;
    }

    try {
      await copyFiles({
        ...sourceConfig,
        files: selectedFiles.map(f => f.Key)
      });
      
      setAlert({
        variant: 'success',
        heading: 'Copy Operation Started',
        text: 'Files are being copied in the background. You will be notified when complete.'
      });

      // Redirect to home page
      navigate('/');

    } catch (error) {
      console.error('Copy operation failed:', error);
      setAlert({
        variant: 'error',
        heading: 'Copy Failed',
        text: 'Failed to start copy operation. Please try again.'
      });
    }
  };

  return (
    <div className="space-y-6">
      <InfoCard
        id="file-copy"
        title="Cloud Connector Tips"
        items={[
          'Ensure your credentials have read access to the source location',
          'Only audio files will be copied and processed',
          'Large files may take some time to process',
          "You'll receive notifications when the copy operation completes",
        ]}
      />

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Cloud Connector</h1>
        <p className="text-gray-600 mt-1">
          Connect to external storage systems and copy audio files for processing
        </p>
      </div>

      {/* Source Type Selection */}
      <div className="card">
        <h3 className="text-sm font-bold text-gray-900 mb-4" data-tour="connector-source">Source Type</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className={`relative flex items-center p-4 border rounded-xl cursor-pointer transition-colors ${
            sourceType === 's3' ? 'border-cloudza-300 bg-cloudza-50/40' : 'border-gray-200 hover:bg-gray-50'
          }`}>
            <input
              type="radio"
              name="sourceType"
              value="s3"
              checked={sourceType === 's3'}
              onChange={(e) => setSourceType(e.target.value)}
              className="sr-only"
            />
            <div className="flex items-center gap-3 min-w-0">
              <span className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 text-lg transition-colors ${
                sourceType === 's3' ? 'bg-cloudza-600 text-white shadow-sm' : 'bg-cloudza-50 text-cloudza-600'
              }`}>
                <FaCloud />
              </span>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">Amazon S3</div>
                <div className="text-xs text-gray-400 mt-0.5">Copy recordings straight from an S3 bucket</div>
              </div>
            </div>
            {sourceType === 's3' && (
              <FaCheckCircle className="ml-auto flex-shrink-0 text-cloudza-600" aria-hidden="true" />
            )}
          </label>

          <label className={`relative flex items-center p-4 border rounded-xl cursor-pointer transition-colors ${
            sourceType === 'ftp' ? 'border-cloudza-300 bg-cloudza-50/40' : 'border-gray-200 hover:bg-gray-50'
          }`}>
            <input
              type="radio"
              name="sourceType"
              value="ftp"
              checked={sourceType === 'ftp'}
              onChange={(e) => setSourceType(e.target.value)}
              className="sr-only"
            />
            <div className="flex items-center gap-3 min-w-0 opacity-70">
              <span className="w-11 h-11 rounded-xl bg-gray-100 text-gray-400 flex items-center justify-center flex-shrink-0 text-lg">
                <FaFolder />
              </span>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">FTP/SFTP</div>
                <div className="text-xs text-gray-400 mt-0.5">Copy from an FTP or SFTP server</div>
              </div>
            </div>
            <span className="ml-auto flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" aria-hidden="true" />
              Coming soon
            </span>
          </label>
        </div>
      </div>

      {/* Configuration Form */}
      <div className="card">
        <h3 className="text-sm font-bold text-gray-900 mb-4">
          {sourceType === 's3' ? 'S3 Configuration' : 'FTP Configuration'}
        </h3>

        {sourceType === 's3' ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div ref={bucketBoxRef}>
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Bucket Name *
                </label>
                {/* The popup anchors to the input itself, not the whole field
                    block, so it opens flush under the box. */}
                <div className="relative">
                  <input
                    type="text"
                    value={sourceConfig.bucket}
                    onChange={(e) => { handleInputChange('bucket', e.target.value); setBucketSearchOpen(true); }}
                    onFocus={() => setBucketSearchOpen(true)}
                    className="input-field"
                    placeholder={bucketsLoading ? 'Loading buckets…' : 'Search or type a bucket name…'}
                    aria-label="Bucket name"
                  />
                  {bucketSearchOpen && (
                    <div className="popup-surface absolute left-0 right-0 top-full mt-1.5 z-30 max-h-56 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg py-1 custom-scrollbar">
                      {bucketsLoading ? (
                        <div className="px-3.5 py-2.5 text-sm text-gray-400 flex items-center gap-2">
                          <InlineSpinner /> Loading buckets in {sourceConfig.region}…
                        </div>
                      ) : bucketsError ? (
                        <div className="px-3.5 py-2.5 text-sm text-red-700">{bucketsError}</div>
                      ) : bucketMatches.length === 0 ? (
                        <div className="px-3.5 py-2.5 text-sm text-gray-400">
                          {regionBuckets.length === 0
                            ? `No buckets found in ${sourceConfig.region}.`
                            : 'No buckets match your search.'}
                        </div>
                      ) : (
                        bucketMatches.map((name) => {
                          const isCurrent = sourceConfig.bucket === name;
                          return (
                            <button
                              key={name}
                              type="button"
                              onClick={() => {
                                handleInputChange('bucket', name);
                                setBucketSearchOpen(false);
                                setSelectedFiles([]);
                                loadBucketContents(name);
                              }}
                              className={`w-full flex items-center gap-2.5 text-left px-3.5 py-2 text-sm cursor-pointer transition-colors ${
                                isCurrent ? 'bg-cloudza-50 text-cloudza-700' : 'text-gray-700 hover:bg-cloudza-50/40'
                              }`}
                            >
                              <FaFolder className={`flex-shrink-0 text-xs ${isCurrent ? 'text-cloudza-600' : 'text-gray-300'}`} />
                              <span className={`truncate ${isCurrent ? 'font-semibold' : ''}`}>{name}</span>
                              {isCurrent && <FaCheckCircle className="ml-auto flex-shrink-0 text-cloudza-600 text-xs" />}
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-1.5">
                  Buckets in the selected region — pick one to load its contents.
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Region
                </label>
                <SelectField
                  value={sourceConfig.region}
                  onChange={(e) => handleInputChange('region', e.target.value)}
                  className="select-field"
                >
                  <option value="us-east-1">US East (N. Virginia)</option>
                  <option value="us-west-2">US West (Oregon)</option>
                  <option value="eu-west-1">Europe (Ireland)</option>
                  <option value="eu-central-1">Europe (Frankfurt)</option>
                  <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                </SelectField>
              </div>
            </div>



            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                Prefix (Optional)
              </label>
              <input
                type="text"
                value={sourceConfig.prefix}
                onChange={(e) => handleInputChange('prefix', e.target.value)}
                className="input-field"
                placeholder="recordings/2024/"
              />
              <p className="text-sm text-gray-500 mt-1">
                Only copy files with this prefix
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Host *
                </label>
                <input
                  type="text"
                  value={sourceConfig.ftpHost}
                  onChange={(e) => handleInputChange('ftpHost', e.target.value)}
                  className="input-field"
                  placeholder="ftp.example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Port
                </label>
                <input
                  type="number"
                  value={sourceConfig.ftpPort}
                  onChange={(e) => handleInputChange('ftpPort', e.target.value)}
                  className="input-field"
                  placeholder="21"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Username *
                </label>
                <input
                  type="text"
                  value={sourceConfig.ftpUsername}
                  onChange={(e) => handleInputChange('ftpUsername', e.target.value)}
                  className="input-field"
                  placeholder="username"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Password *
                </label>
                <input
                  type="password"
                  value={sourceConfig.ftpPassword}
                  onChange={(e) => handleInputChange('ftpPassword', e.target.value)}
                  className="input-field"
                  placeholder="Enter password"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                Remote Path
              </label>
              <input
                type="text"
                value={sourceConfig.ftpPath}
                onChange={(e) => handleInputChange('ftpPath', e.target.value)}
                className="input-field"
                placeholder="/recordings/"
              />
              <p className="text-sm text-gray-500 mt-1">
                Path to the directory containing audio files
              </p>
            </div>
          </div>
        )}

        {/* Connection Status */}
        {connectionStatus && (
          <div className="mt-4">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
              connectionStatus === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}>
              {connectionStatus === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
              {connectionStatus === 'success' ? 'Connection successful' : 'Connection failed'}
            </span>
          </div>
        )}

        {connectionStatus === 'success' && !contentsLoaded && (
          <div className="mt-4">
            {/* Wrapped so the click event doesn't become the bucketOverride param */}
            <button onClick={() => loadBucketContents()} className="btn-outline" disabled={loadingContents}>
              {loadingContents ? 'Loading...' : <><FaFolderOpen /> Load Bucket Contents</>}
            </button>
          </div>
        )}

        {loadingContents && (
          <div className="card !p-0 overflow-hidden mt-4">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2.5">
              <InlineSpinner />
              <h3 className="text-sm font-bold text-gray-900">Loading bucket contents…</h3>
              <span className="text-xs text-gray-400">{sourceConfig.bucket}</span>
            </div>
            <LoadingState variant="rows" header={false} label="Loading bucket contents" />
          </div>
        )}

        {contentsLoaded && !loadingContents && (
          <div className="mt-6">
            <div className="flex items-center gap-2.5 mb-2">
              <h3 className="text-sm font-bold text-gray-900">Files in {sourceConfig.bucket}</h3>
              {bucketContents.length > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-cloudza-50 text-cloudza-700 text-xs font-semibold">
                  {bucketContents.length} {bucketContents.length === 1 ? 'file' : 'files'}
                </span>
              )}
            </div>
            {bucketContents.length === 0 ? (
              <div className="py-10 text-center">
                <div className="mx-auto w-12 h-12 rounded-2xl bg-cloudza-50 text-cloudza-700 flex items-center justify-center">
                  <FaFolderOpen className="text-xl" />
                </div>
                <p className="mt-3 text-sm font-semibold text-gray-800">No files found</p>
                <p className="mt-1 text-sm text-gray-500">
                  This bucket has no objects matching the configured prefix.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto border border-gray-100 rounded-xl">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-5 py-2.5 text-left bg-[#fafafc]">
                        <label
                          className="inline-flex items-center gap-1.5 cursor-pointer whitespace-nowrap select-none"
                          title="Select all files"
                        >
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={toggleSelectAll}
                            aria-label="Select all files"
                            className="h-4 w-4 accent-[#7c3aed] border-gray-200 rounded-lg cursor-pointer"
                          />
                          <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                            All
                          </span>
                        </label>
                      </th>
                      <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">File Name</th>
                      <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Size</th>
                      <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Last Modified</th>
                      <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Storage Class</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {bucketContents.map(item => (
                      <tr key={item.Key} className="hover:bg-cloudza-50/40 transition-colors">
                        <td className="px-5 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedFiles.some(f => f.Key === item.Key)}
                            onChange={() => toggleFile(item)}
                            aria-label={`Select ${item.Key}`}
                            className="h-4 w-4 accent-[#7c3aed] border-gray-200 rounded-lg cursor-pointer"
                          />
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap text-gray-900">{item.Key}</td>
                        <td className="px-5 py-3 whitespace-nowrap tabular-nums text-gray-500">{item.Size}</td>
                        <td className="px-5 py-3 whitespace-nowrap tabular-nums text-gray-500">{item.LastModified}</td>
                        <td className="px-5 py-3 whitespace-nowrap text-gray-500">{item.StorageClass}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {(contentsNextToken || bucketContents.length > 10) && (
              <div className="py-3 text-center">
                <div className="inline-flex items-center gap-2">
                  {contentsNextToken && (
                    <button
                      onClick={() => loadBucketContents(null, { append: true })}
                      disabled={loadingMoreContents}
                      className="btn-outline !py-1.5 !px-4 !text-xs disabled:opacity-50"
                    >
                      {loadingMoreContents ? (
                        <>
                          <InlineSpinner className="mr-2" />
                          Loading...
                        </>
                      ) : (
                        'Load More Files'
                      )}
                    </button>
                  )}
                  {bucketContents.length > 10 && (
                    <button
                      onClick={() => {
                        // Collapse back to the first page; restore its token so
                        // Load More continues from the right place.
                        setBucketContents(prev => prev.slice(0, 10));
                        setContentsNextToken(firstPageNextToken);
                      }}
                      disabled={loadingMoreContents}
                      className="btn-outline !py-1.5 !px-4 !text-xs disabled:opacity-50"
                    >
                      Show Less
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-1.5">
                  Showing {bucketContents.length} file{bucketContents.length === 1 ? '' : 's'}
                  {contentsNextToken ? ' — more available in this bucket' : ''}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-6 mt-6 border-t border-gray-200">
          <button
            onClick={() => navigate('/uploadRecs')}
            className="btn-outline"
          >
            <FaUpload />
            Manual Upload Instead
          </button>

          <div className="flex flex-wrap items-center gap-3">
            {selectedFiles.length > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-cloudza-50 text-cloudza-700 text-xs font-semibold">
                {selectedFiles.length} selected
              </span>
            )}
            <button
              onClick={testConnection}
              disabled={isConnecting}
              className="btn-outline disabled:opacity-50"
            >
              {isConnecting ? (
                <>
                  <InlineSpinner light className="mr-2" />
                  Testing...
                </>
              ) : (
                <>
                  <FaLink />
                  Test Connection
                </>
              )}
            </button>

            <button
              onClick={startCopy}
              disabled={connectionStatus !== 'success' || selectedFiles.length === 0}
              className="btn-primary disabled:opacity-50"
            >
              <FaCopy />
              Start Copy
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileCopy;